import { AgTechButton } from "../../Buttons/AgTechButtons";
import AgTechTitleDetailHeader from "../../Headers/AgTechTitleDetailHeader";
import React, { CSSProperties, createContext, useContext, useEffect, useRef, useState } from "react";
import AgTechPortal, { useAgTechLayerCount } from "../AgTechPortal";
import { agtechDelay } from "@agtech/core/utilities/AgTechUtilities";
import AgTechCloseIcon from "../../Icons/AgTechCloseIcon";
import AgTechWebErrorBoundary, { AgTechLocalWebErrorBoundary } from "../../App/Contexts/AgTechWebErrorBoundary";

export type AgTechModalContentProps = {
    closeModal: () => void
}

export type AgTechModalRenderProps = {
    content: (props: AgTechModalContentProps) => React.ReactNode,
    height?: string,
    width?: string,
    classes?: string,
    showCloseButton?: boolean
}

export type AgTechModalProps = {
    height?: string,
    width?: string,
    maxWidth?: string,
    minWidth?: string,
    classes?: string,
    styles?: CSSProperties,
    closeModal: () => void,
    showCloseButton?: boolean,
    children: React.ReactNode
}

export type AgTechModalContext = {
    growModal: (height?: string, width?: string) => Promise<void>,
    closeModal: (e?: React.MouseEvent<HTMLElement>) => void
}

export const ModalContext = createContext<AgTechModalContext>({
    growModal: async (height?: string, width?: string) => {},
    closeModal: (e?: React.MouseEvent<HTMLElement>) => {}
});

export const useModalContext = () => useContext(ModalContext);

declare type AgTechModalState = {
    isActive: boolean,
    height?: string,
    width?: string
}

const AgTechModal = (props: AgTechModalProps) => {
    const [modalState, updateModalState] = useState<AgTechModalState>({
        isActive: true,
        height: props.height,
        width: props.width
    });

    const layerCount = useAgTechLayerCount();
    const modalContainerRef = useRef<HTMLDivElement>(null);

    const modalContainerSize = useRef<{ height?: number, width?: number }>({
        height: undefined,
        width: undefined
    });

    const modalContext: AgTechModalContext = {
        growModal: async (height?: string, width?: string) => {
            if (modalState.height === height && modalState.width === width) {
                return;
            }

            updateModalState({
                ...modalState,
                height: height ?? modalState.height,
                width: width ?? modalState.width
            });

            await agtechDelay(1200);
        },
        closeModal: (e?: React.MouseEvent<HTMLElement>) => {
            if (e) e.stopPropagation();

            props.closeModal();
        }
    }

    const shouldShowCloseButton = props.showCloseButton ?? true;

    return (
        <AgTechPortal>
            <ModalContext.Provider value={modalContext}>
                <div
                    className={`row-based bg-white rounded shadow-lg flex-initial transition-all duration-500 ease-in-out flex fade-in-400 agtech-app-layer ${props.classes ?? ''}`}
                    style={{
                        height: modalState.height ?? modalContainerSize.current.height,
                        maxHeight: '80%',
                        width: modalState.width ?? modalContainerSize.current.width,
                        minWidth: props.minWidth ?? 360,
                        maxWidth: props.maxWidth ?? '80%',
                        ...props.styles
                    }}
                    onClick={e => e.stopPropagation()}
                    data-layer={layerCount.current + 1}
                    ref={modalContainerRef}
                >
                    <AgTechLocalWebErrorBoundary onError={async () => modalContext.closeModal()}>
                        <div className="stretched row-based relative container">
                            {shouldShowCloseButton ? (
                                <AgTechCloseIcon
                                    onClicked={async () => props.closeModal()} 
                                    classes="absolute mb-auto"
                                    styles={{
                                        top: 16,
                                        right: 16,
                                        zIndex: 100
                                    }}
                                />
                            ) : null}
                            {props.children}
                        </div>
                    </AgTechLocalWebErrorBoundary>
                </div>
            </ModalContext.Provider>
        </AgTechPortal>
    );
}

export type AgTechModalHeaderProps = {
    title: string,
    details?: string
}

export const AgTechModalHeader = (props: AgTechModalHeaderProps) => {
    return (
        <div className="wide bottom-bordered px-5 py-3 container">
            <AgTechTitleDetailHeader
                title={props.title}
                detail={props.details}
                titleFontSize='!text-plus'
                detailFontSize='font-size-small'
                detailMargin={6}
            >
                {/* <AgTechButton
                    icon="times"
                    classes="agtech-modal-close-button icon-only"
                    action={() => props.closeModal()}
                /> */}
            </AgTechTitleDetailHeader>
        </div>
    )
}

export type AgTechStandardModalProps = AgTechModalHeaderProps & {
    body: () => JSX.Element,
    footer?: () => JSX.Element
}

export const AgTechStandardModal = (props: AgTechStandardModalProps) => {
    return (
        <div className="stretched row-based container">
            <AgTechModalHeader {...props} />
            <div className="flexed p-4 py-6 bg-surface overflow-hidden container">
                {props.body()}
            </div>
            { props.footer ? (
                <div className="wide row-based rounded-bottom top-bordered px-5 py-4 bg-white container">
                    {props.footer()}
                </div>
            ) : null}
        </div>
    )
}

export const showStandardModal = (
    props: AgTechModalRenderProps,
    context: {
        showModal: (modalRenderProp: (modalId: string) => React.ReactNode) => string,
        closeModal: (modalId: string) => void
    }
) => {
    return context.showModal((modalId) => (
        <AgTechModal
            closeModal={() => context.closeModal(modalId)}
            height={props.height}
            width={props.width}
            classes={props.classes}
            showCloseButton={props.showCloseButton}
        >
            {props.content({
                closeModal: () => context.closeModal(modalId)
            })}
        </AgTechModal>
    ));
}

export default AgTechModal;